import type { FlagComponent } from 'country-flag-icons/react/3x2';
import * as flags from 'country-flag-icons/react/3x2';
import { AsYouType, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { assign, emit, setup } from 'xstate';
import type { CountryCode } from '@/constants/country';
import type { TFunction } from '@/lib/i18n';
import { phoneNumberSchema } from './zod-schema';

/**
 * 国情報を表す型
 */
export type Country = {
  /** ISO 3166-1 alpha-2 形式の国コード */
  code: CountryCode;
  /** 国際電話番号の国番号（例: "81"） */
  dialCode: string;
  /** 国旗の絵文字 */
  flag: FlagComponent;
};

/**
 * 電話番号の値を表す型
 */
export type PhoneValue = {
  /** 電話番号（ハイフンあり） */
  phoneNumber: string;
  /** 国際電話番号の国番号（例: "81"） */
  dialCode: string;
};

/**
 * 電話入力マシンのコンテキスト型
 */
interface PhoneContext {
  /** 入力された電話番号 */
  phoneNumber: string;
  /** フォーマット済みの電話番号 */
  formattedNumber: string;
  /** 選択された国の国番号 */
  selectedDialCode: string;
  /** 利用可能な国のリスト */
  countries: Country[];
  /** 国選択のオプション */
  countryOptions: Array<{
    key: CountryCode;
    label: string;
    value: string;
  }>;
  /** 国検索のテキスト */
  searchText: string;
  /** エラーメッセージ */
  error: string | null;
  /** ポップオーバーの開閉状態 */
  isOpen: boolean;
}

/**
 * 電話入力マシンのイベント型
 */
type PhoneEvent = {
  type: 'PHONE_NUMBER_CHANGE';
  value: string;
} | {
  type: 'COUNTRY_SEARCH';
  query: string;
} | {
  type: 'DIAL_CODE_SELECT';
  dialCode: string;
} | {
  type: 'TOGGLE_POPOVER';
};

/**
 * 値変更イベント型
 */
type ValueChangeEvent = {
  type: 'valueChange';
  value: PhoneValue;
};

/**
 * 利用可能な国のリストを取得する
 */
const getInitialCountries = () => {
  return getCountries().map(code => ({
    code,
    dialCode: getCountryCallingCode(code),
    flag: flags[code]
  }));
};

/**
 * 電話番号のschema定義
 */
const phoneNumberSchemaByIsRequired = (required: boolean, dialCode: string) => required ? phoneNumberSchema(dialCode).refine(val => val !== '', {
  params: {
    i18n: {
      key: 'custom.required'
    }
  }
}) : phoneNumberSchema(dialCode).optional();

/**
 * 電話番号入力のステートマシンを作成する
 */
export const createPhoneInputMachine = ({
  initialValue,
  required,
  t
}: {
  initialValue: PhoneValue;
  required: boolean;
  t: TFunction;
}) => {
  // 国のリストを初期化
  const countries = getInitialCountries();
  const countryOptions = countries.map(country => ({
    key: country.code,
    label: `${t(`common.country.${country.code}`)} (+${country.dialCode})`,
    value: country.dialCode
  }));
  const machine = setup({
    types: {
      context: {} as PhoneContext,
      events: {} as PhoneEvent,
      emitted: {} as ValueChangeEvent
    },
    actions: {
      // 電話番号の更新アクション
      updatePhoneNumber: assign(({
        event,
        context
      }) => {
        if (event.type !== 'PHONE_NUMBER_CHANGE') return {};

        // 電話番号のフォーマットと検証
        const formatter = new AsYouType({
          defaultCallingCode: context.selectedDialCode
        });
        const formattedNumber = formatter.input(event.value);
        const schema = phoneNumberSchemaByIsRequired(required, context.selectedDialCode);
        const result = schema.safeParse(event.value);

        // 無効な電話番号の場合はエラーメッセージを設定
        const errorMessage = result.success ? null : result.error.issues[0]?.message || 'Invalid phone number';
        return {
          phoneNumber: event.value,
          formattedNumber,
          error: errorMessage
        };
      }),
      // 値の変更を通知
      // @see https://stately.ai/docs/event-emitter
      notifyPhoneNumberChange: emit(({
        context
      }) => ({
        type: 'valueChange' as const,
        value: {
          phoneNumber: context.formattedNumber,
          dialCode: context.selectedDialCode
        }
      })),
      // 選択した国の更新アクション
      updateSelectedDialCode: assign(({
        context,
        event
      }) => {
        if (event.type !== 'DIAL_CODE_SELECT') return {};

        // 国が変更された場合、電話番号を再フォーマットして検証
        const formatter = new AsYouType({
          defaultCallingCode: event.dialCode
        });
        const formattedNumber = formatter.input(context.phoneNumber);
        const schema = phoneNumberSchemaByIsRequired(required, event.dialCode);
        const result = schema.safeParse(context.phoneNumber);
        return {
          selectedDialCode: event.dialCode,
          formattedNumber,
          error: result.success ? null : result.error.issues[0].message,
          isOpen: false
        };
      }),
      // 国変更後の値を通知
      notifyDialCodeChange: emit(({
        context
      }) => ({
        type: 'valueChange' as const,
        value: {
          phoneNumber: context.formattedNumber,
          dialCode: context.selectedDialCode
        }
      })),
      // ポップオーバーの切り替えアクション
      togglePopover: assign(({
        context
      }) => ({
        isOpen: !context.isOpen
      })),
      // 検索テキストの更新アクション
      updateSearchText: assign(({
        event
      }) => ({
        searchText: event.type === 'COUNTRY_SEARCH' ? event.query : ''
      })),
      // バリデーションエラーの設定アクション
      setValidationError: assign(({
        context
      }) => {
        const schema = phoneNumberSchemaByIsRequired(required, context.selectedDialCode);
        const result = schema.safeParse(context.phoneNumber);
        return {
          error: result.success ? null : result.error.issues[0].message
        };
      })
    },
    guards: {
      // 電話番号が有効かどうかを判定するガード
      isValidPhoneNumber: ({
        context
      }) => {
        if (!context.phoneNumber) return true;
        const schema = phoneNumberSchema(context.selectedDialCode);
        return schema.safeParse(context.phoneNumber).success;
      }
    }
  });
  const formatter = new AsYouType({
    defaultCallingCode: initialValue.dialCode
  });
  const formattedNumber = formatter ? formatter.input(initialValue.phoneNumber) : initialValue.phoneNumber;
  return machine.createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QAcAWB7AdmAkp5ArgC4B0AlhADZgDEACgBIDyAcgKID6LAqgLIBCbAEocAwgwCCLAOJsA2gAYAuohTpYZImSyqQAD0QB2AIyGSxgCwLDADgsWAzMYBsxmzYA0IAJ6IHATjMbYwAmAFYwh2dDZ3sbAF94rzQsXHxicipaUSZuFgAVIQBNDgBlNgAZNlF8xRUkEGR1TW1MXQMEQwczY1MA+zcXYK9fBBDDCxIrZwcHcId3YMTkjGw8QlIKahp8pmlpKo46JmOANWE63SaNLR0GjonJmMDnBTDgsJDjEcRTM39IsYwv4LGFDAp-HNlo1VmkNpltjk8oUSuUJEJxJcGtcWndQA9HCQFE4Aq8bAsIoYfghglN-PSbDNDJE3hZjNCUmt0qQAG4AQ0oFD5WkwUBoWLUN1a7V+0SJYQsMS6XRsIVeYWpn3+zhshn8X1BDk+CSSMNS6wy-MFEGFZFF4uM9UluLa91lZlZSu6CzVb2pFhCISmOt1bnBIX8bw5sItvIFFHFyiuzVurvxiGc43lFkZhi+5OJDk1WfzNgUpgs-mCNn80fN3PImCtCYljRT0rdCEzHoVufzDkL-q+JBMi0sE2ZzmciVNmHQEDgVxj3OTUrx+kQAFpnNTt0SFAfD0fD4Y61z4VswKuXTKaQog8TIrY3rqZsXnCR84YJsErECz3ClrxjaIpQNeqa3gGmrGP4JBll8xJAlOoQAbGJDNhA4EdumCABmYThqsCCjqg4Fg7j4iBhPeI5uKq-ixIy+rTqanKAZsTbAVh64dAExgkJ8RrGG8rwHlWVIUQgARmPmkaQl0Mz3mEM7xEAA */
    id: 'phoneInput',
    initial: 'idle',
    context: {
      phoneNumber: initialValue.phoneNumber,
      formattedNumber: formattedNumber,
      selectedDialCode: initialValue.dialCode,
      countries,
      countryOptions,
      searchText: '',
      error: null,
      isOpen: false
    },
    states: {
      idle: {
        on: {
          PHONE_NUMBER_CHANGE: {
            actions: ['updatePhoneNumber', 'notifyPhoneNumberChange']
          },
          DIAL_CODE_SELECT: {
            actions: ['updateSelectedDialCode', 'notifyDialCodeChange']
          },
          TOGGLE_POPOVER: {
            actions: 'togglePopover'
          },
          COUNTRY_SEARCH: {
            actions: 'updateSearchText'
          }
        }
      },
      validating: {
        always: [{
          guard: {
            type: 'isValidPhoneNumber'
          },
          target: 'valid'
        }, {
          target: 'invalid'
        }]
      },
      valid: {
        entry: assign({
          error: null
        }),
        always: {
          target: 'idle'
        }
      },
      invalid: {
        entry: {
          type: 'setValidationError'
        },
        always: {
          target: 'idle'
        }
      }
    }
  });
};